import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Send, User, Clock, Paperclip, AlertCircle, Edit, Plus, ArrowRightLeft, ExternalLink, Download, Image as ImageIcon } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function TicketDetail({ user }) {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subtickets, setSubtickets] = useState([]);
  const [teamupEvents, setTeamupEvents] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [subticketDialogOpen, setSubticketDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  
  // Transfer form
  const [transferForm, setTransferForm] = useState({
    to_department_id: '',
    reason: ''
  });
  
  // Subticket form
  const [subticketForm, setSubticketForm] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category_id: ''
  });
  const [categories, setCategories] = useState([]);
  
  // Merge form
  const [availableTickets, setAvailableTickets] = useState([]);
  const [selectedMergeTicket, setSelectedMergeTicket] = useState('');

  useEffect(() => {
    // AbortController cancela requests pendientes al navegar a otro ticket.
    // Sin esto, si el usuario cambia de ticket mientras vuelven respuestas,
    // la UI puede mostrar mensajes/transfers del ticket anterior sobre el nuevo.
    const controller = new AbortController();
    fetchTicket(controller.signal);
    fetchMessages(controller.signal);
    fetchUsers(controller.signal);
    fetchDepartments(controller.signal);
    fetchSubtickets(controller.signal);
    fetchTeamupEvents(controller.signal);
    fetchTransfers(controller.signal);
    fetchCategories(controller.signal);
    return () => controller.abort();
  }, [ticketId]);

  // Helper: detectar si un error fue porque el request se cancelo intencionalmente.
  const isAbort = (err) => err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError';

  const fetchTicket = async (signal) => {
    try {
      const response = await axiosInstance.get(`/tickets/${ticketId}`, { signal });
      setTicket(response.data);
      setLoading(false);
    } catch (error) {
      if (isAbort(error)) return;
      toast.error('Error al cargar ticket');
      setLoading(false);
    }
  };

  const fetchMessages = async (signal) => {
    try {
      const response = await axiosInstance.get(`/tickets/${ticketId}/messages`, { signal });
      setMessages(response.data);
    } catch (error) {
      if (isAbort(error)) return;
      console.error('Error loading messages');
    }
  };

  const fetchUsers = async (signal) => {
    try {
      const response = await axiosInstance.get('/users', { signal });
      setUsers(response.data);
    } catch (error) {
      if (isAbort(error)) return;
      console.error('Error loading users');
    }
  };

  const fetchDepartments = async (signal) => {
    try {
      const response = await axiosInstance.get('/departments', { signal });
      setDepartments(response.data);
    } catch (error) {
      if (isAbort(error)) return;
      console.error('Error loading departments');
    }
  };

  const fetchSubtickets = async (signal) => {
    try {
      const response = await axiosInstance.get(`/tickets/${ticketId}/subtickets`, { signal });
      setSubtickets(response.data);
    } catch (error) {
      if (isAbort(error)) return;
      console.error('Error loading subtickets');
    }
  };

  const fetchTeamupEvents = async (signal) => {
    try {
      const response = await axiosInstance.get(`/tickets/${ticketId}/teamup-events`, { signal });
      setTeamupEvents(response.data);
    } catch (error) {
      if (isAbort(error)) return;
      console.error('Error loading teamup events:', error);
    }
  };

  const fetchTransfers = async (signal) => {
    try {
      const response = await axiosInstance.get(`/tickets/${ticketId}/transfers`, { signal });
      setTransfers(response.data);
    } catch (error) {
      if (isAbort(error)) return;
      console.error('Error loading transfers');
    }
  };

  const fetchCategories = async (signal) => {
    try {
      const response = await axiosInstance.get('/ticket-categories', { signal });
      setCategories(response.data);
    } catch (error) {
      if (isAbort(error)) return;
      console.error('Error loading categories');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = [];

    for (const file of files) {
      // Convertir a base64
      const reader = new FileReader();
      reader.onload = (event) => {
        newAttachments.push({
          name: file.name,
          type: file.type,
          data: event.target.result
        });
        if (newAttachments.length === files.length) {
          setAttachments([...attachments, ...newAttachments]);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) {
      toast.error('Escribe un mensaje o adjunta un archivo');
      return;
    }

    try {
      await axiosInstance.post(`/tickets/${ticketId}/messages`, {
        ticket_id: ticketId,
        message: newMessage,
        is_internal: false,
        attachments: attachments.map(a => ({
          name: a.name,
          type: a.type,
          data: a.data
        }))
      });
      toast.success('Mensaje enviado');
      setNewMessage('');
      setAttachments([]);
      fetchMessages();
    } catch (error) {
      toast.error('Error al enviar mensaje');
    }
  };

  const handleDownloadAttachment = (attachment) => {
    // Crear un link temporal para descargar el archivo
    const link = document.createElement('a');
    link.href = attachment.data;
    link.download = attachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isImageFile = (type) => {
    return type && type.startsWith('image/');
  };

  const handleUpdateTicket = async (field, value) => {
    try {
      await axiosInstance.put(`/tickets/${ticketId}`, { [field]: value });
      toast.success('Ticket actualizado');
      fetchTicket();
    } catch (error) {
      toast.error('Error al actualizar ticket');
    }
  };

  const handleTransferTicket = async (e) => {
    e.preventDefault();
    if (!transferForm.to_department_id) {
      toast.error('Selecciona un departamento');
      return;
    }
    try {
      await axiosInstance.post(`/tickets/${ticketId}/transfer`, transferForm);
      toast.success('Ticket transferido correctamente');
      setTransferDialogOpen(false);
      setTransferForm({ to_department_id: '', reason: '' });
      fetchTicket();
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al transferir ticket');
    }
  };

  const handleCreateSubticket = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post('/tickets', {
        customer_id: ticket.customer_id,
        parent_ticket_id: ticketId,
        ...subticketForm
      });
      toast.success('Subticket creado correctamente');
      setSubticketDialogOpen(false);
      setSubticketForm({ subject: '', description: '', priority: 'medium', category_id: '' });
      fetchSubtickets();
      // Navegar al subticket recién creado
      navigate(`/tickets/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear subticket');
    }
  };

  const fetchAvailableTicketsForMerge = async () => {
    try {
      if (!ticket) return;
      
      // Get all tickets from the same customer
      const response = await axiosInstance.get('/tickets/sla-status');
      
      // Filter: same customer, not the current ticket, not closed
      const filtered = response.data.filter(t => 
        t.customer_id === ticket.customer_id && 
        t.id !== ticket.id &&
        t.status !== 'closed' &&
        !t.parent_ticket_id  // Only show parent tickets (not subtickets)
      );
      
      setAvailableTickets(filtered);
    } catch (error) {
      console.error('Error fetching tickets for merge:', error);
      toast.error('Error al cargar tickets disponibles');
    }
  };

  const handleMergeTicket = async () => {
    if (!selectedMergeTicket) {
      toast.error('Por favor selecciona un ticket');
      return;
    }

    try {
      const response = await axiosInstance.post(`/tickets/${ticket.id}/merge`, {
        target_ticket_id: selectedMergeTicket
      });
      
      toast.success('Ticket fusionado correctamente');
      setMergeDialogOpen(false);
      
      // Navigate to the parent ticket
      navigate(`/tickets/${selectedMergeTicket}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al fusionar ticket');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: <Badge className="bg-blue-100 text-blue-800">📥 Nuevo</Badge>,
      waiting_client: <Badge className="bg-yellow-100 text-yellow-800">⏸️ Espera Cliente</Badge>,
      waiting_tech: <Badge className="bg-orange-100 text-orange-800">👷 En Progreso</Badge>,
      third_party: <Badge className="bg-purple-100 text-purple-800">🔗 Terceros</Badge>,
      closed: <Badge className="bg-gray-100 text-gray-800">✅ Cerrado</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: <Badge className="bg-red-600 text-white">🔴 URGENTE</Badge>,
      high: <Badge className="bg-orange-500 text-white">🟠 Alta</Badge>,
      medium: <Badge className="bg-yellow-500 text-white">🟡 Media</Badge>,
      low: <Badge className="bg-green-500 text-white">🟢 Baja</Badge>
    };
    return badges[priority] || <Badge>{priority}</Badge>;
  };

  const isSLAOverdue = () => {
    if (!ticket?.sla_due_date || ticket.status === 'closed') return false;
    return new Date(ticket.sla_due_date) < new Date();
  };

  const getTimeRemaining = () => {
    if (!ticket?.sla_due_date) return null;
    const now = new Date();
    const due = new Date(ticket.sla_due_date);
    const diff = due - now;
    
    if (diff < 0) return 'Vencido';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="space-y-4 p-4">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
          <Skeleton className="h-6 w-40" />
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!ticket) {
    return (
      <Layout user={user}>
        <div className="text-center py-12">
          <p className="text-gray-600">Ticket no encontrado</p>
          <Button onClick={() => navigate('/tickets')} className="mt-4">
            Volver a Tickets
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="flex items-start gap-4 flex-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/tickets')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="font-mono text-xl font-bold">{ticket.ticket_number}</span>
                {getPriorityBadge(ticket.priority)}
                {getStatusBadge(ticket.status)}
                {ticket.category_name && (
                  <Badge style={{ backgroundColor: ticket.category_color }}>
                    {ticket.category_name}
                  </Badge>
                )}
                {ticket.current_department_name && (
                  <Badge variant="outline" className="border-purple-500 text-purple-700">
                    📂 {ticket.current_department_name}
                  </Badge>
                )}
              </div>
              <h1 className="text-2xl font-bold text-[#212B35]">{ticket.subject}</h1>
              
              {/* Parent Ticket Indicator */}
              {ticket.parent_ticket_id && (
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                  <ExternalLink className="w-4 h-4" />
                  <span>Subticket de:</span>
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-blue-600 hover:underline"
                    onClick={() => navigate(`/tickets/${ticket.parent_ticket_id}`)}
                  >
                    {ticket.parent_ticket_number}
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTransferDialogOpen(true)}
              disabled={ticket.status === 'closed'}
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Transferir
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSubticketDialogOpen(true)}
              disabled={ticket.status === 'closed' || ticket.parent_ticket_id}
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear Subticket
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setMergeDialogOpen(true);
                fetchAvailableTicketsForMerge();
              }}
              disabled={ticket.status === 'closed'}
              className="border-purple-500 text-purple-700 hover:bg-purple-50"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Fusionar
            </Button>
          </div>
        </div>

        {/* SLA Warning */}
        {isSLAOverdue() && (
          <Card className="border-red-500 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 text-red-800">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">¡Atención! El SLA de este ticket ha vencido</span>
              </div>
            </CardContent>
          </Card>
        )}

        {ticket.sla_due_date && ticket.status !== 'closed' && (
          <Card className={isSLAOverdue() ? 'border-red-300' : 'border-yellow-300'}>
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Tiempo restante SLA:</span>
                <span className={`font-semibold ${isSLAOverdue() ? 'text-red-600' : 'text-yellow-600'}`}>
                  {getTimeRemaining()}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna Principal - Timeline */}
          <div className="lg:col-span-2 space-y-6">
            {/* Descripción Inicial */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Descripción del Problema</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                      {ticket.customer_name[0]}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{ticket.customer_name}</div>
                      <div className="text-xs text-gray-500">
                        {format(new Date(ticket.created_at), "d 'de' MMMM yyyy, HH:mm", { locale: es })}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-800 whitespace-pre-wrap">{ticket.description}</p>
                </div>
              </CardContent>
            </Card>

            {/* Timeline de Mensajes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Conversación</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {messages.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No hay mensajes aún</p>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-500 text-white flex items-center justify-center text-sm font-bold">
                          {msg.user_name?.[0] || 'U'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{msg.user_name || 'Usuario'}</span>
                            {msg.is_internal && (
                              <Badge variant="outline" className="text-xs">Interno</Badge>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {format(new Date(msg.created_at), "d 'de' MMMM yyyy, HH:mm", { locale: es })}
                          </div>
                          <p className="mt-2 text-gray-800 whitespace-pre-wrap">{msg.message}</p>
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {msg.attachments.map((att, idx) => (
                                <div key={idx} className="border rounded-lg p-2 bg-gray-50">
                                  {isImageFile(att.type) ? (
                                    <div className="space-y-2">
                                      <img 
                                        src={att.data} 
                                        alt={att.name}
                                        className="max-w-full h-auto rounded border cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => window.open(att.data, '_blank')}
                                        style={{ maxHeight: '300px' }}
                                      />
                                      <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600 flex items-center gap-1">
                                          <ImageIcon className="w-4 h-4" />
                                          {att.name}
                                        </span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleDownloadAttachment(att)}
                                          className="h-8"
                                        >
                                          <Download className="w-4 h-4 mr-1" />
                                          Descargar
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-700 flex items-center gap-2">
                                        <Paperclip className="w-4 h-4" />
                                        {att.name}
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDownloadAttachment(att)}
                                        className="h-8"
                                      >
                                        <Download className="w-4 h-4 mr-1" />
                                        Descargar
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Historial de Transferencias */}
            {transfers.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5" />
                    Historial de Transferencias
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {transfers.map((transfer) => (
                    <div key={transfer.id} className="border-l-4 border-purple-500 pl-4 py-2 bg-purple-50/30">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold">{transfer.transferred_by_name}</span>
                        <span className="text-gray-600">transferió de</span>
                        <Badge variant="outline">{transfer.from_department_name || 'Sin asignar'}</Badge>
                        <span className="text-gray-600">→</span>
                        <Badge style={{ backgroundColor: departments.find(d => d.id === transfer.to_department_id)?.color || '#6B7280' }}>
                          {transfer.to_department_name}
                        </Badge>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {format(new Date(transfer.created_at), "d 'de' MMMM yyyy, HH:mm", { locale: es })}
                      </div>
                      {transfer.reason && (
                        <p className="mt-2 text-sm text-gray-700 italic">"{transfer.reason}"</p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Subtickets */}
            {subtickets.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Subtickets ({subtickets.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {subtickets.map((sub) => (
                    <div
                      key={sub.id}
                      className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/tickets/${sub.id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-semibold">{sub.ticket_number}</span>
                          <span className="text-sm">{sub.subject}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {sub.status === 'open' && <Badge className="bg-blue-100 text-blue-800">Nuevo</Badge>}
                          {sub.status === 'waiting_tech' && <Badge className="bg-orange-100 text-orange-800">En Progreso</Badge>}
                          {sub.status === 'closed' && <Badge className="bg-gray-100 text-gray-800">Cerrado</Badge>}
                          <ExternalLink className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Teamup Events */}
            {(user?.permissions?.teamup || user?.role === 'admin') && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Eventos de Teamup ({teamupEvents.length})</CardTitle>
                    <Button
                      size="sm"
                      onClick={() => navigate('/teamup')}
                      variant="outline"
                      className="border-indigo-500 text-indigo-600 hover:bg-indigo-500 hover:text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Evento
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {teamupEvents.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No hay eventos asociados a este ticket</p>
                  ) : (
                    teamupEvents.map((event) => (
                      <div
                        key={event.id}
                        className="p-3 border-l-4 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                        style={{ borderLeftColor: event.category_color }}
                        onClick={() => navigate('/teamup')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-sm">{event.title}</span>
                              <Badge 
                                className="text-xs"
                                style={{ backgroundColor: event.category_color, color: 'white' }}
                              >
                                {event.category_name}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-600">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {format(new Date(event.start_date), "dd/MM/yyyy HH:mm")}
                              </span>
                              {event.assigned_to_user_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {event.assigned_to_user_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {event.status === 'planned' && <Badge className="bg-blue-100 text-blue-800">Planificado</Badge>}
                            {event.status === 'in_progress' && <Badge className="bg-yellow-100 text-yellow-800">En Curso</Badge>}
                            {event.status === 'completed' && <Badge className="bg-green-100 text-green-800">Completado</Badge>}
                            <ExternalLink className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            )}

            {/* Responder */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Agregar Respuesta</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Escribe tu respuesta..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={4}
                />
                
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    <Paperclip className="w-4 h-4 mr-2" />
                    Adjuntar ({attachments.length})
                  </Button>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  <Button
                    onClick={handleSendMessage}
                    className="bg-[#9C0F17] hover:bg-[#7A0C12]"
                    disabled={!newMessage.trim() && attachments.length === 0}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Respuesta
                  </Button>
                </div>

                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((att, idx) => (
                      <Badge key={idx} variant="outline">
                        {att.name}
                        <button
                          onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Columna Lateral - Información */}
          <div className="space-y-6">
            {/* Detalles del Ticket */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Detalles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-gray-500">Cliente</Label>
                  <div className="font-medium">{ticket.customer_name}</div>
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Estado</Label>
                  <Select 
                    value={ticket.status} 
                    onValueChange={(value) => handleUpdateTicket('status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">📥 Nuevo</SelectItem>
                      <SelectItem value="waiting_tech">👷 En Progreso</SelectItem>
                      <SelectItem value="waiting_client">⏸️ Espera Cliente</SelectItem>
                      <SelectItem value="third_party">🔗 Terceros</SelectItem>
                      <SelectItem value="closed">✅ Cerrado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Prioridad</Label>
                  <Select 
                    value={ticket.priority} 
                    onValueChange={(value) => handleUpdateTicket('priority', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">🟢 Baja</SelectItem>
                      <SelectItem value="medium">🟡 Media</SelectItem>
                      <SelectItem value="high">🟠 Alta</SelectItem>
                      <SelectItem value="urgent">🔴 Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Asignar a</Label>
                  <Select 
                    value={ticket.assigned_to || 'unassigned'} 
                    onValueChange={(value) => handleUpdateTicket('assigned_to', value === 'unassigned' ? null : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Sin asignar</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-4 border-t">
                  <div className="text-xs text-gray-500 space-y-2">
                    <div>
                      <span className="font-medium">Creado:</span>{' '}
                      {format(new Date(ticket.created_at), "d MMM yyyy HH:mm", { locale: es })}
                    </div>
                    <div>
                      <span className="font-medium">Actualizado:</span>{' '}
                      {format(new Date(ticket.updated_at), "d MMM yyyy HH:mm", { locale: es })}
                    </div>
                    {ticket.closed_at && (
                      <div>
                        <span className="font-medium">Cerrado:</span>{' '}
                        {format(new Date(ticket.closed_at), "d MMM yyyy HH:mm", { locale: es })}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Diálogo de Transferir a Departamento */}
        <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Transferir Ticket a Departamento</DialogTitle>
              <DialogDescription>
                Selecciona el departamento al que deseas transferir este ticket
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleTransferTicket} className="space-y-4">
              <div>
                <Label htmlFor="to_department">Departamento Destino *</Label>
                <Select
                  value={transferForm.to_department_id}
                  onValueChange={(value) => setTransferForm({ ...transferForm, to_department_id: value })}
                  required
                >
                  <SelectTrigger id="to_department">
                    <SelectValue placeholder="Seleccionar departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: dept.color }}
                          />
                          {dept.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="reason">Motivo (opcional)</Label>
                <Textarea
                  id="reason"
                  value={transferForm.reason}
                  onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })}
                  placeholder="Explica por qué se transfiere este ticket..."
                  rows={3}
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setTransferDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-[#9C0F17] hover:bg-[#7A0C12]"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Transferir
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Diálogo de Crear Subticket */}
        <Dialog open={subticketDialogOpen} onOpenChange={setSubticketDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Crear Subticket</DialogTitle>
              <DialogDescription>
                Crea un subticket derivado de {ticket.ticket_number}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSubticket} className="space-y-4">
              <div>
                <Label htmlFor="sub_category">Categoría</Label>
                <Select
                  value={subticketForm.category_id}
                  onValueChange={(value) => setSubticketForm({ ...subticketForm, category_id: value })}
                >
                  <SelectTrigger id="sub_category">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: cat.color }}
                          />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="sub_priority">Prioridad *</Label>
                <Select
                  value={subticketForm.priority}
                  onValueChange={(value) => setSubticketForm({ ...subticketForm, priority: value })}
                >
                  <SelectTrigger id="sub_priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">🟢 Baja</SelectItem>
                    <SelectItem value="medium">🟡 Media</SelectItem>
                    <SelectItem value="high">🟠 Alta</SelectItem>
                    <SelectItem value="urgent">🔴 Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="sub_subject">Asunto *</Label>
                <Input
                  id="sub_subject"
                  value={subticketForm.subject}
                  onChange={(e) => setSubticketForm({ ...subticketForm, subject: e.target.value })}
                  placeholder="Resumen del subticket"
                  required
                />
              </div>

              <div>
                <Label htmlFor="sub_description">Descripción *</Label>
                <Textarea
                  id="sub_description"
                  value={subticketForm.description}
                  onChange={(e) => setSubticketForm({ ...subticketForm, description: e.target.value })}
                  placeholder="Describe el subticket en detalle..."
                  rows={6}
                  required
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSubticketDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-[#9C0F17] hover:bg-[#7A0C12]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Subticket
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Diálogo de Fusionar Ticket */}
        <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Fusionar Ticket</DialogTitle>
              <DialogDescription>
                El ticket <strong>{ticket.ticket_number}</strong> se convertirá en subticket del ticket seleccionado y se cerrará automáticamente.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Info Box */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div className="text-sm text-purple-900">
                    <p className="font-semibold mb-1">¿Qué sucederá al fusionar?</p>
                    <ul className="list-disc list-inside space-y-1 text-purple-800">
                      <li>Este ticket se convertirá en subticket del ticket seleccionado</li>
                      <li>Este ticket se cerrará automáticamente</li>
                      <li>Los subtickets existentes se mantendrán vinculados</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Ticket Selection */}
              <div>
                <Label>Seleccionar Ticket Destino *</Label>
                <p className="text-sm text-gray-600 mb-2">
                  Solo se muestran tickets abiertos del mismo cliente ({ticket.customer_name})
                </p>
                
                {availableTickets.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No hay tickets disponibles para fusionar</p>
                    <p className="text-sm">Todos los tickets de este cliente están cerrados o son subtickets</p>
                  </div>
                ) : (
                  <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                    {availableTickets.map((t) => (
                      <div
                        key={t.id}
                        className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedMergeTicket === t.id ? 'bg-purple-50 border-l-4 border-purple-500' : ''
                        }`}
                        onClick={() => setSelectedMergeTicket(t.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-sm font-semibold">{t.ticket_number}</span>
                              {t.priority === 'urgent' && <Badge className="bg-red-600 text-white text-xs">🔴 URGENTE</Badge>}
                              {t.priority === 'high' && <Badge className="bg-orange-500 text-white text-xs">🟠 Alta</Badge>}
                              {t.priority === 'medium' && <Badge className="bg-yellow-500 text-white text-xs">🟡 Media</Badge>}
                              {t.priority === 'low' && <Badge className="bg-green-500 text-white text-xs">🟢 Baja</Badge>}
                            </div>
                            <p className="text-sm font-medium text-gray-900">{t.title || t.subject}</p>
                            <p className="text-xs text-gray-600 mt-1">
                              Creado: {new Date(t.created_at).toLocaleDateString('es-ES')}
                            </p>
                          </div>
                          {selectedMergeTicket === t.id && (
                            <div className="flex-shrink-0">
                              <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setMergeDialogOpen(false);
                    setSelectedMergeTicket('');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleMergeTicket}
                  disabled={!selectedMergeTicket || availableTickets.length === 0}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Fusionar Ticket
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
